import {
  MessageAttributeValue,
  SendMessageCommand,
  SQSClient,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { eq, and } from "drizzle-orm"
import {
  partyUser,
  partyUserRoleConstraint,
  partyAgency,
  partyB2GProfile,
} from "@tc/drizzle-v2"

const sqsClient = new SQSClient({})
const queueUrl = process.env["OCA_USERS_QUEUE_URL"]

export interface OcaUserDoc {
  id: string
  subId: string
  systemUsername?: string | null
  fullName: string
  email?: string | null
  accountType: string
  status: string
  jobTitle?: string | null
  contactNumber?: string | null
  certificateTypes: string[]
  countries: string[]
  inspectionPorts: string[]
  agencyId?: string | null
  agencyName?: string | null
  profileName?: string | null
  reason?: string | null
  comment?: string | null
  createdAt: number
  updatedAt: number
  createdBy?: string | null
  updatedBy?: string | null
}

export const getOcaUserData = async (
  tx: DbTransaction,
  userId: string,
): Promise<OcaUserDoc | undefined> => {
  const [user] = await tx
    .select()
    .from(partyUser)
    .where(eq(partyUser.id, userId))

  if (!user || !user.g2gUser) {
    return undefined
  }

  // Skip users without valid accountType or status — these are non-G2G users
  // that have g2g_user=true but were never fully set up
  if (!user.accountType || !user.status) {
    return undefined
  }

  // Fetch OCA constraints (multiple rows)
  const constraints = await tx
    .select({
      constraintType: partyUserRoleConstraint.constraintType,
      constraintValue: partyUserRoleConstraint.constraintValue,
      agencyId: partyUserRoleConstraint.agencyId,
    })
    .from(partyUserRoleConstraint)
    .where(
      and(
        eq(partyUserRoleConstraint.userId, userId),
        eq(partyUserRoleConstraint.role, "OCA"),
        eq(partyUserRoleConstraint.active, true),
      ),
    )

  const certificateTypes = constraints
    .filter((c) => c.constraintType === "CERT_TYPE")
    .map((c) => c.constraintValue)

  const countries = constraints
    .filter((c) => c.constraintType === "COUNTRY")
    .map((c) => c.constraintValue)

  const inspectionPorts = constraints
    .filter((c) => c.constraintType === "PORT")
    .map((c) => c.constraintValue)

  const agencyConstraint = constraints.find(
    (c) => c.constraintType === "AGENCY",
  )
  const agencyId =
    agencyConstraint?.agencyId ?? agencyConstraint?.constraintValue ?? null

  let agencyName: string | null = null
  if (agencyId) {
    const [agency] = await tx
      .select({ displayName: partyAgency.displayName })
      .from(partyAgency)
      .where(eq(partyAgency.id, agencyId))
    agencyName = agency?.displayName ?? null
  }

  // Fetch active profile name from b2g_profilename table
  const [profileRow] = await tx
    .select({ profileName: partyB2GProfile.profileName })
    .from(partyB2GProfile)
    .where(
      and(eq(partyB2GProfile.userId, userId), eq(partyB2GProfile.active, true)),
    )
  const profileName = profileRow?.profileName ?? null

  return {
    id: user.id,
    subId: user.sub_id ?? "",
    systemUsername: user.systemUsername ?? null,
    fullName: user.name ?? "",
    email: user.email ?? null,
    accountType: user.accountType ?? "",
    status: user.status ?? "",
    jobTitle: user.jobTitle ?? null,
    contactNumber: user.contactNumber ?? null,
    certificateTypes,
    countries,
    inspectionPorts,
    agencyId,
    agencyName,
    profileName,
    reason: user.statusReason ?? null,
    comment: user.statusComment ?? null,
    createdAt: user.createdAt.getTime(),
    updatedAt: user.updatedAt.getTime(),
    createdBy: user.createdBy ?? null,
    updatedBy: user.updatedBy ?? null,
  }
}

export const saveOcaUserToSearch = async (
  tx: DbTransaction,
  userId: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getOcaUserData(tx, userId)
  if (!doc) {
    console.error(`Failed to prepare OCA user document for id: ${userId}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: queueUrl,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqsClient.send(command)
}
